import { createOrUpdateUserAccess } from 'src/graphql/mutations';
import { listUserAccess } from 'src/graphql/queries';
import { UserPermission, UserType } from '../../types/admin/accessManagement.types';
import { formatApiError } from '../../utils/errorUtils';
import { logger } from '../../utils/logger';
import { fetchGraphQLRequest } from './clients/graphqlClient';

/**
 * Fetches user permissions from API
 * @returns Promise resolving to array of user permissions
 */
export const fetchUserPermissionsAPI = async (): Promise<UserPermission[]> => {
  try {
    logger.info('Fetching user permissions from API');

    const result = await fetchGraphQLRequest<{ listUserAccess: UserPermission[] }>({
      query: listUserAccess,
      variables: {},
      requestName: 'listUserAccess',
      requestType: 'query',
    });
    const resources = result.listUserAccess || [];
    logger.info(`Fetched ${resources.length} user permissions from API`);
    return resources;
  } catch (error) {
    logger.error('Error fetching user permissions:', { error });
    throw formatApiError('Failed to fetch user permissions', error);
  }
};

/**
 * Fetches a single user's permissions from API
 * @param userAlias - User alias to fetch permissions for
 * @returns Promise resolving to user permission or null if not found
 */
export const fetchSingleUserPermissionsAPI = async (userAlias: string): Promise<UserPermission | null> => {
  try {
    logger.info('Fetching single user permissions from API:', { userAlias });

    const result = await fetchGraphQLRequest<{ listUserAccess: UserPermission[] }>({
      query: listUserAccess,
      variables: { user_alias: userAlias },
      requestName: 'listUserAccess',
      requestType: 'query',
    });

    const userPermissions = result.listUserAccess || [];
    const userPermission = userPermissions.find((user) => user.user_alias === userAlias) || null;

    logger.info('Fetched single user permission from API:', {
      userAlias,
      found: !!userPermission,
    });

    return userPermission;
  } catch (error) {
    logger.error('Error fetching single user permissions:', { error, userAlias });
    throw formatApiError('Failed to fetch user permissions', error);
  }
};

/**
 * Updates an existing user permission
 * @param userAlias - User alias to update
 * @param updates - Partial user permission data to update
 * @returns Promise resolving to success response
 */
export const updateUserPermissionAPI = async (
  userAlias: string,
  updatedUserPermissions: UserPermission
): Promise<{ success: boolean; message: string }> => {
  try {
    logger.info('Updating user permission:', { user_alias: userAlias, updatedUserPermissions });

    const result = await fetchGraphQLRequest<{ createOrUpdateUserAccess: { numberOfRecordsUpdated: number } }>({
      query: createOrUpdateUserAccess,
      variables: {
        input: updatedUserPermissions,
      },
      requestName: 'createOrUpdateUserAccess',
      requestType: 'mutation',
      auditConfig: { pageName: 'user_access' },
    });

    const numberOfRecordsUpdated = result.createOrUpdateUserAccess?.numberOfRecordsUpdated ?? 0;

    logger.info('User permission update completed:', {
      userAlias,
      numberOfRecordsUpdated,
    });

    // API call succeeded - return success regardless of records updated count
    // Zero records updated might indicate no changes were needed, which is still a valid success case
    return {
      success: true,
      message:
        numberOfRecordsUpdated > 0
          ? `User permission for ${userAlias} updated successfully (${numberOfRecordsUpdated} record${numberOfRecordsUpdated === 1 ? '' : 's'} updated)`
          : `User permission for ${userAlias} processed successfully (no changes needed)`,
    };
  } catch (error) {
    logger.error('Error updating user permission:', { error, userAlias, updatedUserPermissions });
    throw formatApiError('Failed to update user permission', error);
  }
};

/**
 * Gets user permission details by user alias
 * @param userAlias - User alias to find
 * @param userPermissions - Array of user permissions to search in
 * @returns UserPermission if found, null otherwise
 */
export const getUserPermissionByAlias = (
  userAlias: string,
  userPermissions: UserPermission[]
): UserPermission | null => {
  return userPermissions.find((user) => user.user_alias === userAlias) || null;
};

/**
 * Checks if a user is a Verity Admin based on their user_type
 * @param userAlias - User alias to check
 * @param userPermissions - Array of user permissions to search in
 * @returns boolean indicating if user is a Verity Admin
 */
export const isUserVerityAdmin = (userAlias: string, userPermissions: UserPermission[]): boolean => {
  const userPermission = getUserPermissionByAlias(userAlias, userPermissions);
  return userPermission?.user_type === UserType.VERITY_ADMIN || false;
};

/**
 * Service for managing user permissions
 */
export const accessManagementService = {
  fetchUserPermissionsAPI,
  fetchSingleUserPermissionsAPI,
  updateUserPermissionAPI,
  getUserPermissionByAlias,
  isUserVerityAdmin,
};
